import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Popover,
} from "@mui/material";
import {
  Person,
  CalendarMonth,
  Close,
  CheckCircle,
  YouTube,
  Language,
  SmartToy,
  HelpOutline,
  ArrowForward,
  InfoOutlined,
} from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./CertificationProblemDetails.css";
import CertificationRegistrationLoader from "./CertificationRegistrationLoader";
import LoginModalComponent from "./LoginModalComponent";
import { useDispatch, useSelector } from "react-redux";
import { redirectToPageAction } from "../../../actions/userActions";
import { REDIRECT_CERTIFICATE_PAGE } from "../../../constants/Education/certificateConstants";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { createUserCertificationRegistrationAction } from "../../../actions/MyCertificates/certificatesAction";
import CertificateRegistrationSuccesPopup from "./CertificateRegistrationSuccessPopup";
import { USER_CERTIFICATTION_REGISTRATION_CREATE_RESET } from "../../../constants/MyCertificates/CertificatesConstants";
import { getCurrencySymbol } from "../../../utils/conversion";
import CertificationErrorPopup from "./CertificationErrorPopup";

const submissionSteps = [
  "Push your complete code to a public GitHub repository",
  "Deploy your application to a hosting platform (Vercel, Netlify, or Heroku)",
  "Create a README.md file with setup instructions and feature descriptions",
  "Record a 3-5 minute demo video showcasing all key features",
  "Submit the GitHub repository link, live demo URL, and video link through the platform",
];

const CertificationProblemDetails = ({
  open,
  onClose,
  problemData,
  buttonText = "Start Solving",
  onButtonClick = null,
  pricingData,
  openedFrom,
}) => {
  const history = useHistory();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { redirectUrl } = useSelector((state) => state.redirectUrlStore);
  const { error, success, loading } = useSelector(
    (state) => state.userCreateCertificateRegistrationStore
  );
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const data = problemData;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleStartSolving = (problemData) => {
    if (userInfo) {
      setIsModalOpen(true);
      dispatch(
        createUserCertificationRegistrationAction({
          certificationId: problemData?.certification?._id,
          problemId: problemData?._id,
          paymentDetails: pricingData?.pricingId,
        })
      );
    } else {
      handleLoadingModalOpen();
    }
  };

  const handleInfoClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? "pricing-info-popover" : undefined;

  const drawerVariants = {
    hidden: isMobile ? { y: "100%", opacity: 0 } : { x: "100%", opacity: 0 },
    visible: isMobile
      ? {
          y: 0,
          opacity: 1,
          transition: { type: "spring", damping: 25, stiffness: 200 },
        }
      : {
          x: 0,
          opacity: 1,
          transition: { type: "spring", damping: 25, stiffness: 200 },
        },
    exit: isMobile
      ? { y: "100%", opacity: 0, transition: { duration: 0.3 } }
      : { x: "100%", opacity: 0, transition: { duration: 0.3 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.2, duration: 0.4 },
    },
  };
  const [LoadingModalOpen, setLoadingModalOpen] = useState(false);

  const handleLoadingModalOpen = () => setLoadingModalOpen(true);
  const handleLoadingModalClose = () => setLoadingModalOpen(false);
  const handleLogin = () => {
    history.push("/login");
    dispatch(
      redirectToPageAction(REDIRECT_CERTIFICATE_PAGE, {
        url: `/certificates/details/${data?.certification?._id}`,
        problemData: problemData,
        type: "certifications",
      })
    );
  };

  const handleRedirect = () => {
    history.push("/dashboard");
    dispatch({ type: USER_CERTIFICATTION_REGISTRATION_CREATE_RESET });
  };

  useEffect(() => {
    if (
      redirectUrl?.type === "certifications" &&
      redirectUrl?.url?.length > 0
    ) {
      if (userInfo) {
        setIsModalOpen(true);
        handleStartSolving(redirectUrl?.problemData);
      }
    }
  }, [userInfo, redirectUrl]);

  useEffect(() => {
    if (error) {
      setShowErrorPopup(true);
      setIsModalOpen(false);
    }
  }, [error]);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      className="certificationProblemDetails-drawer"
      PaperProps={{
        className: "certificationProblemDetails-drawerPaper",
        sx: {
          width: isMobile ? "100%" : "500px",
          maxWidth: "100%",
          height: isMobile ? "95vh" : "100vh",
          borderTopLeftRadius: isMobile ? "20px" : 0,
          borderTopRightRadius: isMobile ? "20px" : 0,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            className="certificationProblemDetails-container"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Close Button */}
            <IconButton
              onClick={onClose}
              className="certificationProblemDetails-closeButton"
            >
              <Close />
            </IconButton>

            {/* Scrollable Content */}
            <Box className="certificationProblemDetails-scrollContent">
              <motion.div
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Header Image */}
                <Box className="certificationProblemDetails-imageContainer">
                  <img
                    src={data?.image?.url}
                    alt="Problem"
                    className="certificationProblemDetails-image"
                  />
                </Box>

                {/* Meta Information */}
                <Box className="certificationProblemDetails-metaContainer">
                  <Box className="certificationProblemDetails-metaItem">
                    <Person className="certificationProblemDetails-icon" />
                    <Typography style={{ color: "#fff" }} variant="body2">
                      {data?.totalRegistrations} Registered
                    </Typography>
                  </Box>
                  <Box className="certificationProblemDetails-metaItem">
                    <CalendarMonth className="certificationProblemDetails-icon" />
                    <Typography style={{ color: "#fff" }} variant="body2">
                      {data?.daysToComplete?.displayText}
                    </Typography>
                  </Box>
                  <Box className="certificationProblemDetails-metaItem certificationProblemDetails-pricingItem">
                    <Box className="certificationProblemDetails-pricingContent">
                      <Box className="certificationProblemDetails-freeSection">
                        <Typography
                          variant="body2"
                          className="certificationProblemDetails-freeText"
                        >
                          Free
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={handleInfoClick}
                          className="certificationProblemDetails-infoIcon"
                          aria-describedby={popoverId}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography
                        variant="caption"
                        className="certificationProblemDetails-originalPrice"
                      >
                        {getCurrencySymbol(pricingData?.currency)}
                        {pricingData?.price +
                          (pricingData?.currency === "INR" ? 300 : 50)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Pricing Info Popover */}
                <Popover
                  id={popoverId}
                  open={openPopover}
                  anchorEl={anchorEl}
                  onClose={handleInfoClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  PaperProps={{
                    className: "certificationProblemDetails-popoverPaper",
                  }}
                >
                  <Box className="certificationProblemDetails-popoverContent">
                    <Typography
                      variant="body2"
                      className="certificationProblemDetails-popoverText"
                    >
                      This challenge and submission is{" "}
                      <span style={{ fontWeight: 700, color: "#22c55e" }}>
                        free of cost
                      </span>
                      . Once submitted and reviewed, you can pay{" "}
                      <span style={{ fontWeight: 700, color: "#ffd700" }}>
                        {getCurrencySymbol(pricingData?.currency)}
                        {pricingData?.price}
                      </span>{" "}
                      to download your certificate. Your certificate will be
                      publicly hosted and verifiable.
                    </Typography>
                  </Box>
                </Popover>

                {/* Title and Subtitle */}
                <Box className="certificationProblemDetails-titleContainer">
                  <Typography
                    variant="h5"
                    className="certificationProblemDetails-title"
                  >
                    {data?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="certificationProblemDetails-subtitle"
                  >
                    {data?.subtitle}
                  </Typography>
                </Box>
                {/* Tabs */}
                <Box className="certificationProblemDetails-tabsContainer">
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    className="certificationProblemDetails-tabs"
                  >
                    <Tab
                      label="Problem Statement"
                      className="certificationProblemDetails-tab"
                    />
                    <Tab
                      label="Expected Output"
                      className="certificationProblemDetails-tab"
                    />
                    <Tab
                      label="Resources"
                      className="certificationProblemDetails-tab"
                    />
                  </Tabs>

                  {/* Tab Panels */}
                  <Box className="certificationProblemDetails-tabPanel">
                    {activeTab === 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Typography
                          variant="body1"
                          className="certificationProblemDetails-text"
                        >
                          {data?.problemStatement?.description}
                        </Typography>

                        {/* Key Pointers Section */}
                        <Box className="certificationProblemDetails-sectionContainer">
                          <Typography
                            variant="h6"
                            className="certificationProblemDetails-sectionTitle"
                          >
                            Key Pointers
                          </Typography>
                          <Box className="certificationProblemDetails-pointersContainer">
                            {data?.problemStatement?.keyPointers?.map(
                              (pointer, index) => (
                                <Box
                                  key={index}
                                  className="certificationProblemDetails-pointerItem"
                                >
                                  <HelpOutline className="certificationProblemDetails-pointerIcon" />
                                  <Typography
                                    style={{ fontFamily: "Questrial" }}
                                  >
                                    {pointer}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Box>
                        </Box>

                        {/* UI Reference Section - Optional */}
                        {data?.problemStatement?.uiReferences &&
                          data?.problemStatement?.uiReferences?.length > 0 && (
                            <Box className="certificationProblemDetails-sectionContainer">
                              <Typography
                                variant="h6"
                                className="certificationProblemDetails-sectionTitle"
                              >
                                UI Reference
                              </Typography>
                              <Box className="certificationProblemDetails-carouselContainer">
                                <Swiper
                                  modules={[Navigation, Pagination]}
                                  spaceBetween={16}
                                  slidesPerView={1}
                                  navigation
                                  pagination={{
                                    clickable: true,
                                    bulletClass:
                                      "certificationProblemDetails-swiperBullet",
                                    bulletActiveClass:
                                      "certificationProblemDetails-swiperBulletActive",
                                  }}
                                  className="certificationProblemDetails-swiper"
                                >
                                  {data?.problemStatement?.uiReferences?.map(
                                    (image, index) => (
                                      <SwiperSlide key={index}>
                                        <img
                                          src={image?.url}
                                          alt={`UI Reference ${index + 1}`}
                                          className="certificationProblemDetails-carouselImage"
                                        />
                                      </SwiperSlide>
                                    )
                                  )}
                                </Swiper>
                                <Box className="certificationProblemDetails-carouselHint">
                                  <ArrowForward className="certificationProblemDetails-carouselHintIcon" />
                                  <Typography variant="caption">
                                    Swipe to see{" "}
                                    {
                                      data?.problemStatement?.uiReferences
                                        ?.length
                                    }{" "}
                                    reference images
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                      </motion.div>
                    )}

                    {activeTab === 1 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* What You'll Build Section */}
                        <Box className="certificationProblemDetails-sectionContainer">
                          <Typography
                            variant="h6"
                            className="certificationProblemDetails-sectionTitle"
                          >
                            What You'll Build
                          </Typography>
                          <Box className="certificationProblemDetails-buildContainer">
                            {data?.expectedOutput?.buildPoints?.map(
                              (item, index) => (
                                <Box
                                  key={index}
                                  className="certificationProblemDetails-buildCard"
                                >
                                  <CheckCircle className="certificationProblemDetails-buildIcon" />
                                  <Typography
                                    style={{
                                      fontFamily: "Questrial",
                                      color: "#fff",
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Box>
                        </Box>

                        {/* How It Should Look - Optional */}
                        {data?.expectedOutput?.uiReferences &&
                          data?.expectedOutput?.uiReferences?.length > 0 && (
                            <Box className="certificationProblemDetails-sectionContainer">
                              <Typography
                                variant="h6"
                                className="certificationProblemDetails-sectionTitle"
                              >
                                How It Should Look
                              </Typography>
                              <Box className="certificationProblemDetails-carouselContainer">
                                <Swiper
                                  modules={[Navigation, Pagination]}
                                  spaceBetween={16}
                                  slidesPerView={1}
                                  navigation
                                  pagination={{
                                    clickable: true,
                                    bulletClass:
                                      "certificationProblemDetails-swiperBullet",
                                    bulletActiveClass:
                                      "certificationProblemDetails-swiperBulletActive",
                                  }}
                                  className="certificationProblemDetails-swiper"
                                >
                                  {data?.expectedOutput?.uiReferences?.map(
                                    (image, index) => (
                                      <SwiperSlide key={index}>
                                        <img
                                          src={image}
                                          alt={`Expected Output ${index + 1}`}
                                          className="certificationProblemDetails-carouselImage"
                                        />
                                      </SwiperSlide>
                                    )
                                  )}
                                </Swiper>
                                <Box className="certificationProblemDetails-carouselHint">
                                  <ArrowForward className="certificationProblemDetails-carouselHintIcon" />
                                  <Typography variant="caption">
                                    Swipe to see{" "}
                                    {data?.expectedOutput?.uiReferences?.length}{" "}
                                    example images
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}

                        {/* How Do I Submit Section */}
                        <Box className="certificationProblemDetails-sectionContainer">
                          <Typography
                            variant="h6"
                            className="certificationProblemDetails-sectionTitle"
                          >
                            How Do I Submit?
                          </Typography>
                          <Box className="certificationProblemDetails-submissionContainer">
                            {submissionSteps?.map((step, index) => (
                              <Box
                                key={index}
                                className="certificationProblemDetails-submissionStep"
                              >
                                <Box className="certificationProblemDetails-stepNumber">
                                  {index + 1}
                                </Box>
                                <Typography
                                  style={{
                                    fontFamily: "Questrial",
                                    color: "#fff",
                                  }}
                                >
                                  {step}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </motion.div>
                    )}

                    {activeTab === 2 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box className="certificationProblemDetails-resourcesContainer">
                          {/* YouTube Resources */}
                          {data?.resources?.videoTutorials &&
                            data?.resources?.videoTutorials?.length > 0 && (
                              <Box className="certificationProblemDetails-resourceSection">
                                <Box className="certificationProblemDetails-resourceHeader">
                                  <YouTube className="certificationProblemDetails-resourceIcon youtube" />
                                  <Typography
                                    style={{
                                      color: "#fff",
                                      fontFamily: "Questrial",
                                    }}
                                    variant="h6"
                                  >
                                    Video Tutorials
                                  </Typography>
                                </Box>
                                {data?.resources?.videoTutorials?.map(
                                  (video, index) => (
                                    <a
                                      href={video?.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ textDecoration: "none" }}
                                    >
                                      <Box
                                        key={index}
                                        className="certificationProblemDetails-resourceCard"
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            className="certificationProblemDetails-resourceTitle"
                                            component="a"
                                            href={video?.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {video?.title}
                                          </Typography>
                                          <OpenInNewIcon
                                            sx={{
                                              color: "#fff",
                                              fontSize: "20px",
                                              marginBottom: "10px",
                                            }}
                                          />
                                        </div>

                                        {video?.description && (
                                          <Typography
                                            variant="caption"
                                            className="certificationProblemDetails-resourceDescription"
                                          >
                                            {video?.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </a>
                                  )
                                )}
                              </Box>
                            )}

                          {/* Web Resources */}
                          {data?.resources?.documentation &&
                            data?.resources?.documentation?.length > 0 && (
                              <Box className="certificationProblemDetails-resourceSection">
                                <Box className="certificationProblemDetails-resourceHeader">
                                  <Language className="certificationProblemDetails-resourceIcon web" />
                                  <Typography
                                    style={{
                                      color: "#fff",
                                      fontFamily: "Questrial",
                                    }}
                                    variant="h6"
                                  >
                                    Documentation & Articles
                                  </Typography>
                                </Box>
                                {data?.resources?.documentation?.map(
                                  (resource, index) => (
                                    <a
                                      href={resource?.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ textDecoration: "none" }}
                                    >
                                      <Box
                                        key={index}
                                        className="certificationProblemDetails-resourceCard"
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            className="certificationProblemDetails-resourceTitle"
                                            component="a"
                                            href={resource?.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {resource?.title}
                                          </Typography>
                                          <OpenInNewIcon
                                            sx={{
                                              color: "#fff",
                                              fontSize: "20px",
                                              marginBottom: "10px",
                                            }}
                                          />
                                        </div>

                                        {resource?.description && (
                                          <Typography
                                            variant="caption"
                                            className="certificationProblemDetails-resourceDescription"
                                          >
                                            {resource?.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </a>
                                  )
                                )}
                              </Box>
                            )}

                          {/* AI Tools */}
                          {data?.resources?.aiAssistantGuideline && (
                            <Box className="certificationProblemDetails-resourceSection">
                              <Box className="certificationProblemDetails-resourceHeader">
                                <SmartToy className="certificationProblemDetails-resourceIcon ai" />
                                <Typography
                                  style={{
                                    color: "#fff",
                                    fontFamily: "Questrial",
                                  }}
                                  variant="h6"
                                >
                                  {data?.resources?.aiHelp?.title ||
                                    "AI Assistant Tools"}
                                </Typography>
                              </Box>
                              <Box className="certificationProblemDetails-resourceCard">
                                <Typography
                                  variant="body2"
                                  className="certificationProblemDetails-text"
                                >
                                  {data?.resources?.aiAssistantGuideline}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </motion.div>
                    )}
                  </Box>
                  <p
                    style={{ margin: "10px 20px" }}
                    className="certificationTimeline-bottomInfo"
                  >
                    More than 100+ students got internship offers after
                    completing their project.
                  </p>
                </Box>
              </motion.div>
            </Box>

            {/* Fixed Bottom Button */}
            <div className="certificationProblemDetails-buttonContainer">
              <Button
                fullWidth
                onClick={() =>
                  openedFrom === "dashboard"
                    ? onButtonClick()
                    : handleStartSolving(problemData)
                }
                className="certificationProblemDetails-startButton"
              >
                {buttonText}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <CertificationRegistrationLoader
        isOpen={isModalOpen}
        imageSrc="/images/certificate-loading.png"
        onClose={() => {
          setIsModalOpen(false);
          if (success) setShowSuccessPopup(true);
        }}
      />
      <LoginModalComponent
        open={LoadingModalOpen}
        onClose={handleLoadingModalClose}
        imageSrc="/images/login-modal.png"
        imageAlt="Welcome"
        text="You’re all set! Log in to start your selected challenge and continue your certification journey."
        onLogin={handleLogin}
      />
      <CertificateRegistrationSuccesPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        imageUrl="https://example.com/success-image.png"
        onRedirect={handleRedirect}
      />
      <CertificationErrorPopup
        open={showErrorPopup}
        onClose={() => {
          setShowErrorPopup(false);
          dispatch({ type: USER_CERTIFICATTION_REGISTRATION_CREATE_RESET });
        }}
        errorMessage={error}
      />
    </Drawer>
  );
};

export default CertificationProblemDetails;
